import React from "react";
import { Box, Card, Chip, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import type {
  JotCoachGap,
  JotCoachProviderQuestion,
  JotCoachDocumentationAnalysis
} from "@features/jotcoach";

export interface FeedbackItem {
  target_type: "gap" | "question" | "session_overall";
  target_id: string;
  is_useful: boolean;
  comment?: string;
  note_id?: string | null;
  section_id?: string | null;
}

interface JotCoachFeedbackItemProps {
  feedbackItem: FeedbackItem;
  documentationAnalysis?: JotCoachDocumentationAnalysis;
}

/**
 * Helper function to find the related gap or question from documentation analysis
 */
const findRelatedItem = (
  feedbackItem: FeedbackItem,
  docAnalysis?: JotCoachDocumentationAnalysis
): JotCoachGap | JotCoachProviderQuestion | null => {
  if (!docAnalysis) return null;

  if (feedbackItem.target_type === "gap") {
    return docAnalysis.gaps?.find(
      (g: JotCoachGap) => g.id === feedbackItem.target_id
    ) ?? null;
  } else if (feedbackItem.target_type === "question") {
    return docAnalysis.provider_questions?.find(
      (q: JotCoachProviderQuestion) => q.id === feedbackItem.target_id
    ) ?? null;
  }

  return null;
};

/**
 * Get the border color based on gap priority or type
 */
const getBorderColor = (
  targetType: string,
  relatedItem: JotCoachGap | JotCoachProviderQuestion | null
): string => {
  if (targetType === "gap" && relatedItem && "priority" in relatedItem) {
    const priority = relatedItem.priority;
    if (priority === "High") return "#d32f2f"; // error.main
    if (priority === "Medium") return "#f57c00"; // warning.main
    return "#fbc02d"; // warning.light
  }
  if (targetType === "question") return "#0288d1"; // info.light
  return "#1976d2"; // primary.main - for session_overall
};

/**
 * Display user feedback with visual context showing what item was reviewed
 */
export const JotCoachFeedbackItem: React.FC<JotCoachFeedbackItemProps> = ({
  feedbackItem,
  documentationAnalysis,
}) => {
  const relatedItem = findRelatedItem(feedbackItem, documentationAnalysis);
  const borderColor = getBorderColor(feedbackItem.target_type, relatedItem);

  // Get type label and content to display
  const getTypeLabel = (): string => {
    if (feedbackItem.target_type === "gap") return "Gap";
    if (feedbackItem.target_type === "question") return "Question";
    return "Overall Session";
  };

  const renderItemContent = () => {
    if (feedbackItem.target_type === "session_overall") {
      return (
        <Box
          sx={{
            p: 2,
            backgroundColor: "#e3f2fd",
            borderRadius: 1,
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            JotCoach Analysis
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {documentationAnalysis?.tone_for_provider || "Overall session feedback"}
          </Typography>
        </Box>
      );
    }

    if (!relatedItem) {
      return (
        <Box
          sx={{
            p: 2,
            backgroundColor: "#f5f5f5",
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            Item not found (ID: {feedbackItem.target_id})
          </Typography>
        </Box>
      );
    }

    // Display gap
    if ("title" in relatedItem) {
      const gap = relatedItem as JotCoachGap;
      return (
        <Box
          sx={{
            p: 2,
            backgroundColor: "#e3f2fd",
            borderRadius: 1,
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {gap.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {gap.description}
          </Typography>
        </Box>
      );
    }

    // Display question
    if ("question" in relatedItem) {
      const question = relatedItem as JotCoachProviderQuestion;
      return (
        <Box
          sx={{
            p: 2,
            backgroundColor: "#e3f2fd",
            borderRadius: 1,
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {question.question}
          </Typography>
          {question.why_needed && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {question.why_needed}
            </Typography>
          )}
        </Box>
      );
    }

    return null;
  };

  return (
    <Card
      sx={{
        borderLeft: `4px solid ${borderColor}`,
        mb: 2,
      }}
    >
      <Box sx={{ p: 2 }}>
        {/* Header with type badge and useful indicator */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <Chip
            label={getTypeLabel()}
            size="small"
            sx={{
              backgroundColor: borderColor,
              color: "#fff",
              fontWeight: "bold",
            }}
          />
          <Chip
            icon={feedbackItem.is_useful ? <CheckIcon /> : <CloseIcon />}
            label={feedbackItem.is_useful ? "Useful" : "Not Useful"}
            size="small"
            color={feedbackItem.is_useful ? "success" : "error"}
            variant="outlined"
          />
          {feedbackItem.section_id && (
            <Chip
              label={`Section: ${feedbackItem.section_id}`}
              size="small"
              variant="outlined"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
              }}
            />
          )}
        </Box>

        {/* Content from the item being reviewed */}
        {renderItemContent()}

        {/* Provider comment if provided */}
        {feedbackItem.comment && (
          <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #e0e0e0" }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight="bold"
              display="block"
              gutterBottom
            >
              Provider Comment:
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              fontStyle="italic"
            >
              "{feedbackItem.comment}"
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
};
